import { useCallback, useEffect, useRef, useState } from 'react';
import { idpFetchErrorMessage } from './errors';
import type {
  IdpAutofillPayload,
  IdpConfidenceField,
  IdpDocumentType,
  IdpFetchFn,
  IdpJobStatus,
} from './types';

const DEFAULT_THRESHOLD = 0.8;
const POLL_INTERVAL_MS = 1500;
const MAX_POLLS = 60;

interface UseDocumentIntelligenceOptions {
  documentType: IdpDocumentType;
  context?: string;
  targetSchema?: Record<string, string>;
  fetchWithAuth: IdpFetchFn;
  uploadPath?: string;
  reviewThreshold?: number;
  resultPathPrefix?: string;
}

function failJob(
  message: string,
  setStatus: (status: IdpJobStatus) => void,
  setError: (error: string | null) => void,
  clearPoll: () => void
): null {
  setStatus('failed');
  setError(message);
  clearPoll();
  return null;
}

export function useDocumentIntelligence({
  documentType,
  context,
  targetSchema,
  fetchWithAuth,
  uploadPath = '/api/v1/idp/upload',
  reviewThreshold = DEFAULT_THRESHOLD,
  resultPathPrefix = '/api/v1/idp',
}: UseDocumentIntelligenceOptions) {
  const [status, setStatus] = useState<IdpJobStatus>('idle');
  const [jobId, setJobId] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, IdpConfidenceField>>({});
  const [overallConfidence, setOverallConfidence] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollGenerationRef = useRef(0);

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => clearPoll(), [clearPoll]);

  const pollAutofill = useCallback(
    async (id: string): Promise<IdpAutofillPayload | null> => {
      const generation = pollGenerationRef.current;
      let attempts = 0;

      return new Promise((resolve) => {
        pollRef.current = setInterval(async () => {
          if (generation !== pollGenerationRef.current) {
            clearPoll();
            resolve(null);
            return;
          }

          attempts += 1;
          try {
            const resultRes = await fetchWithAuth(`${resultPathPrefix}/result/${id}`);
            const resultJson = await resultRes.json().catch(() => ({}));

            if (!resultRes.ok) {
              resolve(
                failJob(
                  idpFetchErrorMessage(resultRes, resultJson, 'Could not check document status'),
                  setStatus,
                  setError,
                  clearPoll
                )
              );
              return;
            }

            const resultStatus = resultJson.data?.status as string | undefined;
            const resultError = resultJson.data?.error as string | undefined;

            if (resultStatus === 'failed') {
              resolve(
                failJob(resultError || 'Document processing failed', setStatus, setError, clearPoll)
              );
              return;
            }

            if (resultStatus !== 'completed') {
              if (attempts >= MAX_POLLS) {
                resolve(
                  failJob('Document processing timed out', setStatus, setError, clearPoll)
                );
              }
              return;
            }

            const autofillRes = await fetchWithAuth(`${resultPathPrefix}/autofill/${id}`);
            const autofillJson = await autofillRes.json().catch(() => ({}));

            if (!autofillRes.ok) {
              resolve(
                failJob(
                  idpFetchErrorMessage(autofillRes, autofillJson, 'Could not load extracted fields'),
                  setStatus,
                  setError,
                  clearPoll
                )
              );
              return;
            }

            clearPoll();
            const data = autofillJson.data;
            const mapped: Record<string, IdpConfidenceField> = data?.fields || {};
            const payload: IdpAutofillPayload = {
              jobId: id,
              fields: mapped,
              overallConfidence: data?.overall_confidence ?? 0,
              needsManualReview:
                data?.needs_manual_review ??
                Object.values(mapped).some(
                  (f) => f.needs_review || f.confidence < reviewThreshold
                ),
            };
            setFields(mapped);
            setOverallConfidence(payload.overallConfidence);
            setStatus('completed');
            resolve(payload);
          } catch {
            resolve(
              failJob(
                'Lost connection while waiting for document processing',
                setStatus,
                setError,
                clearPoll
              )
            );
          }
        }, POLL_INTERVAL_MS);
      });
    },
    [clearPoll, fetchWithAuth, reviewThreshold, resultPathPrefix]
  );

  const uploadFile = useCallback(
    async (file: File): Promise<IdpAutofillPayload | null> => {
      pollGenerationRef.current += 1;
      clearPoll();
      setError(null);
      setStatus('uploading');
      setFields({});
      setJobId(null);

      const form = new FormData();
      form.append('file', file);
      form.append('documentType', documentType);
      if (context) form.append('context', context);
      if (targetSchema) form.append('targetSchema', JSON.stringify(targetSchema));

      try {
        const res = await fetchWithAuth(uploadPath, { method: 'POST', body: form });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          return failJob(
            idpFetchErrorMessage(res, json, 'Document upload failed'),
            setStatus,
            setError,
            clearPoll
          );
        }
        const id = json.data?.job_id as string;
        if (!id) {
          return failJob('Document upload succeeded but no job id was returned', setStatus, setError, clearPoll);
        }
        setJobId(id);
        setStatus('processing');
        return pollAutofill(id);
      } catch {
        return failJob('Document upload failed — check your connection', setStatus, setError, clearPoll);
      }
    },
    [clearPoll, context, documentType, fetchWithAuth, pollAutofill, targetSchema, uploadPath]
  );

  const reset = useCallback(() => {
    pollGenerationRef.current += 1;
    clearPoll();
    setStatus('idle');
    setJobId(null);
    setFields({});
    setOverallConfidence(0);
    setError(null);
  }, [clearPoll]);

  return {
    status,
    jobId,
    fields,
    overallConfidence,
    error,
    uploadFile,
    reset,
  };
}
